import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, ArrowLeft, Plus, Pencil, Trash2, ChevronRight, ChevronDown } from 'lucide-react';

const getCurrentMonthLabel = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

const currencySymbol = (code) => {
  const c = String(code || 'GBP').toUpperCase();
  if (c === 'GBP') return '£';
  if (c === 'USD') return '$';
  if (c === 'EUR') return '€';
  return '£';
};

const formatAmountWithSymbol = (amount, currencyCode) => {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  return `${currencySymbol(currencyCode)}${safe.toFixed(2)}`;
};

const parseDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const LinkBuildingDashboard = () => {
  const { user, isAdmin } = useAuth();
  const isLinkManager = user?.role === 'link_manager';
  const isCompliance = user?.role === 'compliance';
  const canManage = isAdmin || isLinkManager;
  const canSeeFinancial = canManage;
  const canSeeLinkContentFinancial = Boolean(user);
  const canImportLinkBuilding = canManage;
  const canEditOwnContent = !isCompliance && Boolean(user);
  const isContentUrlOnlyEdit = canEditOwnContent && !canManage;
  const hasActionsColumn = canManage || canEditOwnContent;

  const canEditContentRow = (row) => {
    if (isCompliance) return false;
    if (canManage) return true;
    return Number(row.writer_id) === Number(user?.id);
  };

  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [contentRows, setContentRows] = useState([]);
  const [monthlyTargets, setMonthlyTargets] = useState([]);
  const [selectedMonthId, setSelectedMonthId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [editingContentId, setEditingContentId] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [contentForm, setContentForm] = useState({
    client_id: '',
    month_id: '',
    article_title: '',
    writer_id: '',
    writer_name_override: '',
    content_url: '',
    website_url: '',
    dr_score: '',
    tf_score: '',
    cf_score: '',
    anchor_text: '',
    page_linked: '',
    contact_info: '',
    price: '',
    currency_code: 'GBP',
    qc: false,
    is_approved: false,
    is_live: false,
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const baseRequests = [
        axios.get('/api/link-building/months'),
        axios.get(isAdmin ? '/api/clients?include_inactive=true' : '/api/clients'),
        axios.get('/api/link-building/content'),
        axios.get('/api/link-building/client-monthly-targets')
      ];
      const [monthsRes, clientsRes, contentRes, monthlyTargetsRes] = await Promise.all(baseRequests);
      let usersData = [];
      if (canManage) {
        const usersRes = await axios.get('/api/users');
        usersData = usersRes.data || [];
      }

      const linkBuildingClients = (clientsRes.data || []).filter(
        (client) => client.uses_link_building !== 0 && client.uses_link_building !== false
      );
      setMonths(monthsRes.data || []);
      setClients(linkBuildingClients);
      setUsers(usersData);
      setContentRows(contentRes.data || []);
      setMonthlyTargets(monthlyTargetsRes.data || []);
    } catch (error) {
      toast.error('Failed to load link-building data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, canManage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!selectedMonthId && months.length > 0) {
      const currentMonthLabel = getCurrentMonthLabel();
      const matching = months.find((m) => m.month_label === currentMonthLabel);
      setSelectedMonthId(String((matching || months[months.length - 1]).id));
    }
  }, [months, selectedMonthId]);

  const selectedMonth = useMemo(
    () => months.find((m) => String(m.id) === String(selectedMonthId)),
    [months, selectedMonthId]
  );
  const selectedClient = useMemo(
    () => clients.find((c) => String(c.id) === String(selectedClientId)),
    [clients, selectedClientId]
  );

  const visibleClients = useMemo(() => {
    const selectedMonthStart = parseDateSafe(selectedMonth?.month_start);
    return clients.filter((client) => {
      const isInactive = String(client.status || '').toLowerCase() === 'inactive';
      if (!isInactive) return true;

      const inactiveFrom = parseDateSafe(client.inactive_from_month);
      if (!inactiveFrom || !selectedMonthStart) return false;
      return selectedMonthStart < inactiveFrom;
    });
  }, [clients, selectedMonth]);

  useEffect(() => {
    if (selectedClientId && !visibleClients.some((c) => String(c.id) === String(selectedClientId))) {
      setSelectedClientId('');
    }
  }, [visibleClients, selectedClientId]);

  useEffect(() => {
    setExpandedRowId(null);
  }, [selectedClientId, selectedMonthId]);

  const monthTargetMap = useMemo(() => {
    const map = new Map();
    monthlyTargets.forEach((row) => {
      if (selectedMonthId && String(row.month_id) === String(selectedMonthId)) {
        map.set(String(row.client_id), Number(row.monthly_target || 0));
      }
    });
    return map;
  }, [monthlyTargets, selectedMonthId]);

  const contentForMonth = useMemo(
    () =>
      contentRows.filter((row) => {
        if (!selectedMonthId) return true;
        return String(row.month_id) === String(selectedMonthId);
      }),
    [contentRows, selectedMonthId]
  );

  const clientStats = useMemo(() => {
    return visibleClients.map((client) => {
      const rows = contentForMonth.filter((r) => String(r.client_id) === String(client.id));
      const totalLinks = rows.length;
      const totalCost = rows.reduce((sum, r) => sum + Number(r.price_gbp || r.price || 0), 0);
      const avgCost = totalLinks > 0 ? totalCost / totalLinks : 0;
      return {
        client,
        totalLinks,
        totalCost,
        avgCost,
        monthlyTarget: Number(monthTargetMap.get(String(client.id)) || 0)
      };
    });
  }, [visibleClients, contentForMonth, monthTargetMap]);

  const overallStats = useMemo(() => {
    const totalLinks = contentForMonth.length;
    const totalCost = contentForMonth.reduce((sum, row) => sum + Number(row.price_gbp || row.price || 0), 0);
    const avgCost = totalLinks > 0 ? totalCost / totalLinks : 0;
    return { totalLinks, totalCost, avgCost };
  }, [contentForMonth]);

  const clientContentRows = useMemo(
    () =>
      contentRows.filter((row) => {
      if (!selectedClientId) return false;
      if (String(row.client_id) !== String(selectedClientId)) return false;
      if (selectedMonthId && String(row.month_id) !== String(selectedMonthId)) return false;
      return true;
      }),
    [contentRows, selectedClientId, selectedMonthId]
  );

  const getStatusRowClass = (row) => {
    if (row.is_live) return 'bg-green-50';
    if (row.is_approved) return 'bg-orange-50';
    if (row.qc) return 'bg-yellow-50';
    return '';
  };

  const truncateArticleTitle = (title) => {
    const text = String(title || '');
    if (text.length <= 40) return text;
    return `${text.slice(0, 37)}...`;
  };

  const resetContentForm = useCallback(() => {
    setEditingContentId(null);
    setContentForm({
      client_id: selectedClientId || '',
      month_id: selectedMonthId || '',
      article_title: '',
      writer_id: '',
      writer_name_override: '',
      content_url: '',
      website_url: '',
      dr_score: '',
      tf_score: '',
      cf_score: '',
      anchor_text: '',
      page_linked: '',
      contact_info: '',
      price: '',
      currency_code: 'GBP',
      qc: false,
      is_approved: false,
      is_live: false,
      notes: ''
    });
  }, [selectedClientId, selectedMonthId]);

  const openClientDashboard = (clientId) => {
    setSelectedClientId(String(clientId));
    resetContentForm();
  };

  const openAddContentModal = () => {
    resetContentForm();
    setShowContentModal(true);
  };

  const openEditContentModal = (row) => {
    setEditingContentId(row.id);
    setContentForm({
      client_id: row.client_id ? String(row.client_id) : '',
      month_id: row.month_id ? String(row.month_id) : '',
      article_title: row.article_title || '',
      writer_id: row.writer_id ? String(row.writer_id) : '',
      writer_name_override: row.writer_name_override || '',
      content_url: row.content_url || '',
      website_url: row.website_url || '',
      dr_score: String(row.dr_score || ''),
      tf_score: String(row.tf_score || ''),
      cf_score: String(row.cf_score || ''),
      anchor_text: row.anchor_text || '',
      page_linked: row.page_linked || '',
      contact_info: row.contact_info || '',
      price: String(row.price || ''),
      currency_code: row.currency_code || 'GBP',
      qc: Boolean(row.qc),
      is_approved: Boolean(row.is_approved),
      is_live: Boolean(row.is_live),
      notes: row.notes || ''
    });
    setShowContentModal(true);
  };

  const toggleRowExpanded = (rowId) => {
    setExpandedRowId((prev) => (String(prev) === String(rowId) ? null : rowId));
  };

  const saveContent = async (e) => {
    e.preventDefault();
    try {
      if (editingContentId && isContentUrlOnlyEdit) {
        await axios.put(`/api/link-building/content/${editingContentId}`, {
          content_url: contentForm.content_url
        });
        toast.success('Content URL updated');
      } else {
        const payload = {
          ...contentForm,
          client_id: contentForm.client_id ? Number(contentForm.client_id) : null,
          month_id: contentForm.month_id ? Number(contentForm.month_id) : null,
          writer_id: contentForm.writer_id ? Number(contentForm.writer_id) : null,
          dr_score: Number(contentForm.dr_score || 0),
          tf_score: Number(contentForm.tf_score || 0),
          cf_score: Number(contentForm.cf_score || 0),
          price: Number(contentForm.price || 0)
        };

        if (editingContentId) {
          await axios.put(`/api/link-building/content/${editingContentId}`, payload);
          toast.success('Content updated');
        } else {
          await axios.post('/api/link-building/content', payload);
          toast.success('Content created');
        }
      }

      setShowContentModal(false);
      resetContentForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save content');
    }
  };

  const deleteContent = async (id) => {
    if (!window.confirm('Delete this content row?')) return;
    try {
      await axios.delete(`/api/link-building/content/${id}`);
      toast.success('Content deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete content');
    }
  };

  const toggleQcStatus = async (row) => {
    try {
      await axios.put(`/api/link-building/content/${row.id}`, { qc: !row.qc });
      toast.success('QC status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update QC status');
    }
  };

  const importLinkBuildingTargets = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please choose a CSV file');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post('/api/link-building/content/import', formData);
      const {
        inserted = 0,
        updatedExisting = 0,
        skipped = 0,
        failed = 0,
        deduped = 0,
        errors = []
      } = response.data || {};
      toast.success(
        `Import complete. Inserted: ${inserted}, Updated: ${updatedExisting}, Duplicates removed: ${deduped}, Skipped: ${skipped}, Failed: ${failed}`
      );
      setShowImportModal(false);
      setImportFile(null);
      setImportResults({ inserted, updatedExisting, skipped, failed, deduped, errors });
      setShowImportResultsModal(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import link-building targets');
    }
  };

  const monthLabel = selectedMonth?.month_label || 'All Months';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!selectedClientId ? (
        <>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <h1 className="text-2xl font-bold">Link Building Monitor</h1>
            <div className="flex items-center gap-2">
              <input
                type="month"
                className="input"
                value={monthLabel}
                onChange={(e) => {
                  const next = months.find((m) => m.month_label === e.target.value);
                  if (next) setSelectedMonthId(String(next.id));
                }}
              />
            </div>
          </div>

          {canSeeFinancial && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-xs uppercase text-gray-500">Total Links</p>
                  <p className="text-3xl font-bold text-primary-600">{overallStats.totalLinks}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Total Cost</p>
                  <p className="text-3xl font-bold text-primary-600">{formatAmountWithSymbol(overallStats.totalCost, 'GBP')}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Average Cost</p>
                  <p className="text-3xl font-bold text-primary-600">{formatAmountWithSymbol(overallStats.avgCost, 'GBP')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Clients</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {clientStats.map((entry) => (
                <button
                  key={entry.client.id}
                  type="button"
                  onClick={() => openClientDashboard(entry.client.id)}
                  className="text-left rounded-lg p-6 transition cursor-pointer bg-gray-50 border hover:-translate-y-0.5 hover:shadow"
                  style={{ borderColor: '#e1e5e9' }}
                >
                  <h3 className="font-semibold text-gray-900 mb-3">{entry.client.name}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500 uppercase text-xs">Links</p>
                      <p className="font-semibold text-primary-600">{entry.totalLinks}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase text-xs">Monthly Target</p>
                      <p className="font-semibold text-primary-600">{entry.monthlyTarget}</p>
                    </div>
                    {canSeeFinancial && (
                      <>
                        <div>
                          <p className="text-gray-500 uppercase text-xs">Cost</p>
                          <p className="font-semibold text-primary-600">{formatAmountWithSymbol(entry.totalCost, 'GBP')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 uppercase text-xs">Avg Cost</p>
                          <p className="font-semibold text-primary-600">{formatAmountWithSymbol(entry.avgCost, 'GBP')}</p>
                        </div>
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <h1 className="text-2xl font-bold">{selectedClient?.name || 'Client'}</h1>
            <div className="flex items-center gap-2">
              <input
                type="month"
                className="input"
                value={monthLabel}
                onChange={(e) => {
                  const next = months.find((m) => m.month_label === e.target.value);
                  if (next) setSelectedMonthId(String(next.id));
                }}
              />
              <button
                className="btn btn-secondary inline-flex items-center gap-2 h-10 px-4 whitespace-nowrap"
                onClick={() => setSelectedClientId('')}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-900">Link Content</h2>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-semibold">
                  {monthLabel}
                </span>
                <div className="flex items-center gap-4 text-sm text-gray-700">
                  <span className="font-semibold">Status:</span>
                  <label className="inline-flex items-center gap-2">
                    <span className="inline-block h-3 w-3 rounded-sm bg-yellow-50 border border-yellow-200" />
                    QC&apos;d
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <span className="inline-block h-3 w-3 rounded-sm bg-orange-50 border border-orange-200" />
                    Sent For Approval
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <span className="inline-block h-3 w-3 rounded-sm bg-green-50 border border-green-200" />
                    Live
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canImportLinkBuilding && (
                  <button
                    type="button"
                    className="btn btn-secondary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap shrink-0"
                    onClick={() => {
                      setImportFile(null);
                      setImportResults(null);
                      setShowImportResultsModal(false);
                      setShowImportModal(true);
                    }}
                  >
                    Import CSV
                  </button>
                )}
                {canManage && (
                  <button
                    type="button"
                    className="btn btn-primary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap shrink-0"
                    onClick={openAddContentModal}
                  >
                    <Plus className="h-4 w-4 shrink-0" />
                    Add Content
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Writer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Website</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anchor Text</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page Linked</th>
                    {canSeeLinkContentFinancial && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    )}
                    {hasActionsColumn && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clientContentRows.length === 0 ? (
                    <tr>
                      <td colSpan={canSeeLinkContentFinancial ? (hasActionsColumn ? 8 : 7) : (hasActionsColumn ? 7 : 6)} className="px-4 py-8 text-center text-sm text-gray-500">
                        No link content for this month.
                      </td>
                    </tr>
                  ) : (
                    clientContentRows.map((row) => {
                      const isExpanded = String(expandedRowId) === String(row.id);
                      const detailColSpan = canSeeLinkContentFinancial ? (hasActionsColumn ? 8 : 7) : (hasActionsColumn ? 7 : 6);
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`${getStatusRowClass(row)} cursor-pointer`} onClick={() => toggleRowExpanded(row.id)}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="flex items-center gap-2" title={row.article_title || ''}>
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                                )}
                                <span>{truncateArticleTitle(row.article_title || '—')}</span>
                                {!row.anchor_text && (
                                  <span title="Needs Anchor Text">
                                    <AlertCircle className="h-4 w-4 text-amber-500" />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.writer_name_override || row.writer_full_name || row.writer_username || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              {row.content_url ? (
                                <a
                                  href={row.content_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-secondary py-1 px-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View
                                </a>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {row.website_url ? (
                                <a
                                  href={row.website_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-secondary py-1 px-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View
                                </a>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.anchor_text || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              {row.page_linked ? (
                                <a
                                  href={row.page_linked}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-secondary py-1 px-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View
                                </a>
                              ) : '-'}
                            </td>
                            {canSeeLinkContentFinancial && (
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {formatAmountWithSymbol(row.price, row.currency_code)}
                              </td>
                            )}
                            {hasActionsColumn && (
                              <td className="px-4 py-3 text-right text-sm">
                                <div className="inline-flex gap-2">
                                  {canEditContentRow(row) && (
                                    <button
                                      className="btn btn-secondary py-1 px-3"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditContentModal(row);
                                      }}
                                      title={isContentUrlOnlyEdit ? 'Add Content URL' : 'Edit content'}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                          {isExpanded && (
                            <tr className={getStatusRowClass(row)}>
                              <td colSpan={detailColSpan} className="px-4 pb-4 pt-0">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="border border-gray-200 rounded-lg p-3">
                                    <p className="text-xs uppercase text-gray-500 mb-2">Status</p>
                                    <div className="grid grid-cols-3 gap-2 text-sm text-gray-700">
                                      <div>
                                        <p className="text-xs text-gray-500">QC</p>
                                        <p>{row.qc ? 'Yes' : 'No'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Sent For Approval</p>
                                        <p>{row.is_approved ? 'Yes' : 'No'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Live</p>
                                        <p>{row.is_live ? 'Yes' : 'No'}</p>
                                      </div>
                                    </div>
                                    {isCompliance && (
                                      <div className="mt-3">
                                        <button
                                          type="button"
                                          className={`btn text-xs ${row.qc ? 'btn-secondary' : 'btn-primary'}`}
                                          onClick={() => toggleQcStatus(row)}
                                        >
                                          QC&apos;d: {row.qc ? 'Yes' : 'No'} (Toggle)
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="border border-gray-200 rounded-lg p-3">
                                    <p className="text-xs uppercase text-gray-500 mb-2">Metrics</p>
                                    <div className="grid grid-cols-3 gap-2 text-sm text-gray-700">
                                      <div>
                                        <p className="text-xs text-gray-500">DR</p>
                                        <p>{Number(row.dr_score || 0)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">TF</p>
                                        <p>{Number(row.tf_score || 0)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">CF</p>
                                        <p>{Number(row.cf_score || 0)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="md:col-span-2 border border-gray-200 rounded-lg p-3">
                                    <p className="text-xs uppercase text-gray-500 mb-2">Contact</p>
                                    <p className="text-sm text-gray-700">{row.contact_info || '—'}</p>
                                  </div>
                                  <div className="md:col-span-2 border border-gray-200 rounded-lg p-3">
                                    <p className="text-xs uppercase text-gray-500 mb-2">Notes</p>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{row.notes || '—'}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showContentModal && (canManage || (editingContentId && isContentUrlOnlyEdit)) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingContentId
                ? (isContentUrlOnlyEdit ? 'Add Content URL' : 'Edit Content')
                : 'Add New Content'}
            </h3>
            <form onSubmit={saveContent} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {isContentUrlOnlyEdit ? (
                <>
                  <div className="md:col-span-4">
                    <label className="label" htmlFor="lb-article-title-readonly">Article Title</label>
                    <input
                      id="lb-article-title-readonly"
                      className="input bg-gray-50"
                      value={contentForm.article_title}
                      readOnly
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="label" htmlFor="lb-content-url">Content URL</label>
                    <input
                      id="lb-content-url"
                      className="input"
                      placeholder="https://..."
                      value={contentForm.content_url}
                      onChange={(e) => setContentForm({ ...contentForm, content_url: e.target.value })}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Paste the published article URL once the content is live.
                    </p>
                  </div>
                </>
              ) : (
                <>
              <select className="input" value={contentForm.month_id} onChange={(e) => setContentForm({ ...contentForm, month_id: e.target.value })} required>
                <option value="">Select Month</option>
                {months.map((m) => <option key={m.id} value={m.id}>{m.month_label}</option>)}
              </select>
              <input className="input md:col-span-2" placeholder="Article Title *" value={contentForm.article_title} onChange={(e) => setContentForm({ ...contentForm, article_title: e.target.value })} required />
              <select className="input" value={contentForm.writer_id} onChange={(e) => setContentForm({ ...contentForm, writer_id: e.target.value })}>
                <option value="">Select Writer</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
              </select>

              <input className="input md:col-span-2" placeholder="Content URL" value={contentForm.content_url} onChange={(e) => setContentForm({ ...contentForm, content_url: e.target.value })} />
              <input className="input md:col-span-2" placeholder="Website URL" value={contentForm.website_url} onChange={(e) => setContentForm({ ...contentForm, website_url: e.target.value })} />

              <input className="input" placeholder="DR" type="number" value={contentForm.dr_score} onChange={(e) => setContentForm({ ...contentForm, dr_score: e.target.value })} />
              <input className="input" placeholder="TF" type="number" value={contentForm.tf_score} onChange={(e) => setContentForm({ ...contentForm, tf_score: e.target.value })} />
              <input className="input" placeholder="CF" type="number" value={contentForm.cf_score} onChange={(e) => setContentForm({ ...contentForm, cf_score: e.target.value })} />
              <input className="input" placeholder="Anchor Text" value={contentForm.anchor_text} onChange={(e) => setContentForm({ ...contentForm, anchor_text: e.target.value })} />

              <input className="input md:col-span-2" placeholder="Page Linked (URL)" value={contentForm.page_linked} onChange={(e) => setContentForm({ ...contentForm, page_linked: e.target.value })} />
              <input className="input md:col-span-2" placeholder="Contact" value={contentForm.contact_info} onChange={(e) => setContentForm({ ...contentForm, contact_info: e.target.value })} />

              <input className="input" type="number" step="0.01" placeholder="Price" value={contentForm.price} onChange={(e) => setContentForm({ ...contentForm, price: e.target.value })} />
              <div>
                <label className="label" htmlFor="lb-currency-select">Currency</label>
                <select
                  id="lb-currency-select"
                  className="input-currency"
                  value={contentForm.currency_code}
                  onChange={(e) => setContentForm({ ...contentForm, currency_code: e.target.value })}
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input id="qc-box" type="checkbox" checked={contentForm.qc} onChange={(e) => setContentForm({ ...contentForm, qc: e.target.checked })} />
                <label htmlFor="qc-box">QC</label>
              </div>
              <div className="flex items-center gap-2">
                <input id="approve-box" type="checkbox" checked={contentForm.is_approved} onChange={(e) => setContentForm({ ...contentForm, is_approved: e.target.checked })} />
                <label htmlFor="approve-box">Sent For Approval</label>
              </div>
              <div className="flex items-center gap-2">
                <input id="live-box" type="checkbox" checked={contentForm.is_live} onChange={(e) => setContentForm({ ...contentForm, is_live: e.target.checked })} />
                <label htmlFor="live-box">Live</label>
              </div>

              <textarea className="input md:col-span-4" rows="3" placeholder="Notes / Comments" value={contentForm.notes} onChange={(e) => setContentForm({ ...contentForm, notes: e.target.value })} />
                </>
              )}

              <div className="md:col-span-4 flex flex-wrap items-center gap-3">
                <button type="submit" className="btn btn-primary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap">
                  {editingContentId ? (isContentUrlOnlyEdit ? 'Save Content URL' : 'Save Content') : 'Add Content'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap"
                  onClick={() => { setShowContentModal(false); resetContentForm(); }}
                >
                  Cancel
                </button>
                {editingContentId && canManage && (
                  <button
                    type="button"
                    className="btn bg-red-600 text-white hover:bg-red-700 inline-flex flex-row items-center justify-center gap-2 h-10 px-4 whitespace-nowrap"
                    onClick={() => {
                      const idToDelete = editingContentId;
                      setShowContentModal(false);
                      resetContentForm();
                      deleteContent(idToDelete);
                    }}
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    Delete
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && canImportLinkBuilding && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Link Building Targets (CSV)</h3>
            <p className="text-sm text-gray-600 mb-2">
              Required headers: Client Name, Month Name, Month Year, Article Title.
              Optional: Writer Full Name, External Writer, URLs, QC, DR/TF/CF, Anchor Text, Page Linked, Contact,
              Price, Currency, Live, Approved, Notes.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <a
                href="/templates/link-building-import-template.csv"
                className="text-primary-600 hover:text-primary-700 underline"
                download
              >
                Download import template
              </a>
            </p>
            <form onSubmit={importLinkBuildingTargets} className="space-y-4">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="input"
                required
              />
              <div className="flex gap-3">
                <button type="submit" className="btn btn-primary">Import</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportResultsModal && canImportLinkBuilding && importResults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Link Building Import Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Inserted</p>
                <p className="text-lg font-semibold text-gray-900">{importResults.inserted}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Updated Existing</p>
                <p className="text-lg font-semibold text-gray-900">{importResults.updatedExisting}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Duplicates Removed</p>
                <p className="text-lg font-semibold text-gray-900">{importResults.deduped ?? 0}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Skipped</p>
                <p className="text-lg font-semibold text-gray-900">{importResults.skipped}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Failed</p>
                <p className="text-lg font-semibold text-gray-900">{importResults.failed}</p>
              </div>
            </div>
            {Array.isArray(importResults.errors) && importResults.errors.length > 0 ? (
              <div className="border border-gray-200 rounded-lg max-h-72 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {importResults.errors.map((item, idx) => (
                      <tr key={`${item.row}-${idx}`}>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.row}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No row-level errors reported.</p>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowImportResultsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkBuildingDashboard;
